/**
 * "Kennenlernmodus": the guided tour of the demo (steps: @fsv/data tours.ts).
 *
 * Only in a demo session, never in a real one — the provider checks the
 * session mode AND the backend kind, like the demo band. It starts by itself
 * the first time a persona is opened (remembered per persona in this
 * browser), and can be paused, resumed and restarted from the demo band and
 * from the help. Progress lives in localStorage only; the demo has no server.
 *
 * The coach mark highlights the real element (`data-tour="…"`) and shows a
 * card. Nothing blocks the page: some steps ask the person to tap the drawing
 * themselves, and the tour moves on when the next element appears.
 */
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import {
  parseTourProgress,
  tourPersonaOf,
  resumeStep,
  shouldAutoStart,
  tourStorageKey,
  tourSteps,
  type TourPersona,
  type TourProgress,
  type TourStep,
} from '@fsv/data';
import { useBackend, useSessionState } from '../data/hooks';
import { TourOverlay } from './TourOverlay';

export interface TourApi {
  /** A demo session with a persona that has a tour. */
  readonly available: boolean;
  readonly progress: TourProgress | null;
  readonly steps: readonly TourStep[];
  /** From the first step. */
  start(): void;
  /** From where it was paused (or the start). */
  resume(): void;
}

const NO_TOUR: TourApi = {
  available: false,
  progress: null,
  steps: [],
  start: () => undefined,
  resume: () => undefined,
};
const TourContext = createContext<TourApi>(NO_TOUR);

export function useTour(): TourApi {
  return useContext(TourContext);
}

function read(persona: TourPersona | null): TourProgress | null {
  if (!persona) return null;
  try {
    return parseTourProgress(window.localStorage.getItem(tourStorageKey(persona)));
  } catch {
    return null;
  }
}

function write(persona: TourPersona, progress: TourProgress): void {
  try {
    window.localStorage.setItem(tourStorageKey(persona), JSON.stringify(progress));
  } catch {
    /* private mode or full: the tour still runs, it just forgets */
  }
}

export function TourProvider({ children }: { children: ReactNode }) {
  const state = useSessionState();
  const backend = useBackend();
  const persona: TourPersona | null = tourPersonaOf(state.status === 'signed-in' ? state.session : null, backend.kind);
  const steps = useMemo(() => (persona ? tourSteps(persona, 'web') : []), [persona]);
  const [progress, setProgress] = useState<TourProgress | null>(() => read(persona));

  // A persona opened for the first time starts its tour; switching persona reads theirs.
  useEffect(() => {
    if (!persona) return setProgress(null);
    const stored = read(persona);
    if (shouldAutoStart(stored)) {
      const first: TourProgress = { status: 'running', step: 0 };
      write(persona, first);
      setProgress(first);
    } else setProgress(stored);
  }, [persona]);

  // Which step the person asked for (start, resume, Weiter, Zurück): only then
  // does the tour open the step's page. A reload or another page of the app
  // with a running tour must not drag the person back to the step.
  const requested = useRef<number | null>(null);
  const set = useCallback(
    (next: TourProgress, open = false) => {
      if (!persona) return;
      requested.current = open && next.status === 'running' ? next.step : null;
      write(persona, next);
      setProgress(next);
    },
    [persona],
  );

  const api = useMemo<TourApi>(
    () =>
      persona
        ? {
            available: true,
            progress,
            steps,
            start: () => set({ status: 'running', step: 0 }, true),
            resume: () => set({ status: 'running', step: resumeStep(progress, steps.length) }, true),
          }
        : NO_TOUR,
    [persona, progress, steps, set],
  );

  const running = persona !== null && progress?.status === 'running' && steps.length > 0;
  const index = running ? Math.min(progress.step, steps.length - 1) : 0;
  const step = steps[index];

  return (
    <TourContext.Provider value={api}>
      {children}
      {running && step && (
        <TourOverlay
          key={step.id}
          step={step}
          index={index}
          count={steps.length}
          autoOpen={requested.current === index}
          onBack={() => set({ status: 'running', step: Math.max(0, index - 1) }, true)}
          onNext={() =>
            set(
              index + 1 >= steps.length
                ? { status: 'done', step: 0 }
                : { status: 'running', step: index + 1 },
              true,
            )
          }
          onPause={() => set({ status: 'paused', step: index })}
          onEnd={() => set({ status: 'done', step: 0 })}
        />
      )}
    </TourContext.Provider>
  );
}
